import type { ChangeEvent } from 'react';
import { ArrowDownOutlined, ArrowUpOutlined, SearchOutlined } from '@ant-design/icons';
import cx from 'classnames';
import { useTranslation } from 'react-i18next';
import { Namespace } from '@data/index';
import { OrderDirection, QuestionOrderBy } from '@data/sdk/match';
import {
  AntdInput,
  AntdSelect,
  AntdSkeleton,
  AntdSwitch,
  AntdTypography,
  Button,
} from '@ui/components';
import { SORT_OPTIONS } from './constants';

type QuestionsSearchProps = {
  count: number | undefined;
  currentVersionOnly: boolean;
  handleChangeOrder: (value: string) => void;
  handleChangeOrderBy: (value: string) => void;
  handleCurrentVersionChange: (value: boolean) => void;
  handleSearchChange: (e: ChangeEvent<HTMLInputElement>) => void;
  isIcdCategoryIds: boolean;
  orderBy: QuestionOrderBy;
  orderByIcdSpecificCount: boolean;
  orderDirection: OrderDirection;
  searchString: string;
};

const { Text } = AntdTypography;

export const QuestionsSearch = ({
  count,
  orderBy,
  searchString,
  orderDirection,
  isIcdCategoryIds,
  currentVersionOnly,
  orderByIcdSpecificCount,
  handleSearchChange,
  handleChangeOrder,
  handleChangeOrderBy,
  handleCurrentVersionChange,
}: QuestionsSearchProps) => {
  const { t } = useTranslation([Namespace.ADMIN]);

  return (
    <div className="p-4 flex items-center flex-wrap gap-4">
      <div className="flex items-center">
        <AntdInput
          allowClear
          value={searchString}
          onChange={handleSearchChange}
          suffix={<SearchOutlined className="text-base-900" />}
          className="w-full md:w-[400px]"
          placeholder={t('questions_library.search.placeholder')}
          data-testid="questions-search-input"
        />
        <Text className="ml-2 flex items-center gap-1">
          {t('questions_library.questions_found', { count })}:
          {typeof count === 'number' ? count : <AntdSkeleton.Button className="h-5 w-5" />}
        </Text>
      </div>
      <div className="ml-auto flex items-center">
        <Text className="mr-1">{t('questions_library.sort_by')}:</Text>
        <AntdSelect
          className="w-[165px]"
          onChange={handleChangeOrderBy}
          value={
            orderBy === QuestionOrderBy.TrialCount && orderByIcdSpecificCount
              ? 'icdTrialCount'
              : orderBy
          }
          data-testid="questions-sort-select"
        >
          {SORT_OPTIONS.map((option, index) =>
            // first option (relevance(distance)) is not displayed when search string is empty
            // since BE throws 400 if orderBy is relevance (distance) and searchString is empty
            // last option (icdTrialCount) is not displayed when icdCategoryIds is empty
            (index === 0 && !searchString) ||
            (index === SORT_OPTIONS.length - 1 && !isIcdCategoryIds) ? null : (
              <AntdSelect.Option key={option.value} value={option.value}>
                {t(`questions_library.sorting.${option.label}`)}
              </AntdSelect.Option>
            ),
          )}
        </AntdSelect>
        <Button
          type="text"
          className="gap-0 ml-1 px-2"
          onClick={() =>
            handleChangeOrder(
              orderDirection === OrderDirection.Asc ? OrderDirection.Desc : OrderDirection.Asc,
            )
          }
        >
          <ArrowUpOutlined
            className={cx('text-base-600', {
              'text-primary-600': orderDirection === OrderDirection.Asc,
            })}
          />
          <ArrowDownOutlined
            className={cx('text-base-600', {
              'text-primary-600': orderDirection === OrderDirection.Desc,
            })}
          />
        </Button>
      </div>

      <AntdSwitch
        value={currentVersionOnly}
        onChange={handleCurrentVersionChange}
        checkedChildren={t('questions_library.version_switch.ai')}
        unCheckedChildren={t('questions_library.version_switch.all')}
        data-testid="questions-current-version-switch"
      />
    </div>
  );
};
